import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { MedicineApiResponse } from "../types";
import { AdminOrderService } from "../../../../../../services/api/AdminOrderService/admin.order.service";
import toast from "react-hot-toast";

export interface Medication {
  id: number;
  name: string;
  dosage: number;
  details: string;
  duration: number;
  salt?:string;
}

interface Props {
  medication: Medication;
  onRemove: () => void;
  onUpdate: (updated: Partial<Medication>) => void;
  isInputRow: boolean;
}

const MedicationRow: React.FC<Props> = ({
  medication,
  onRemove,
  onUpdate,
  isInputRow,
}) => {
  const [medicineName, setMedicineName] = useState(medication.name || "");
  const [showMedicineList, setShowMedicineList] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState("");
  const [salt, setSalt] = useState(medication.salt||"");

  const [selectedMedicine, setSelectedMedicine] =
    useState<MedicineApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [medicineList, setMedicineList] = useState<
    MedicineApiResponse[] | null
  >(null);
  const [filteredMedicines, setFilteredMedicines] = useState<
    MedicineApiResponse[]
  >([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchDebounce.trim()) {
        fetchMedicines(searchDebounce.trim());
      } else {
        setFilteredMedicines([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeout);
  }, [searchDebounce]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/\s{2,}/g, " ");
    setMedicineName(inputValue);
    setSearchDebounce(inputValue);
    onUpdate({ name: inputValue ,salt:salt});
    setSalt("");

    setShowMedicineList(!!inputValue.trim());
  };

  const fetchMedicines = async (query: string) => {
    setLoading(true);
    try {
      const response = await AdminOrderService.getAllMedicines(query);
      if (response.success === "invalid") {
        toast.error(response.message);
        return;
      }
      if (response.success && response.data) {
        setFilteredMedicines(response.data.slice(0, 5)); // limit to 5
      } else {
        console.error("Failed to fetch medicines:", response.problem);
      }
    } catch (error) {
      console.error("Error fetching medicines:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedicines("");
  }, []);

  const selectMedicine = (medicine: MedicineApiResponse) => {
    const medicineName = medicine.name || "";
    setMedicineName(medicineName);
    setSalt(medicine.saltDetails);
    setSelectedMedicine(medicine);
    setShowMedicineList(false);
    onUpdate({ name: medicineName,salt:medicine.saltDetails });
  };

  useEffect(() => {
    setMedicineName(medication.name || "");
    setSalt(medication.salt??"");
  }, [medication.name]);

  return (
    <div className="w-full bg-gray-50 rounded-lg p-3 mb-3">
      {/* First Row: Name and Remove Button */}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-full ${isInputRow ? "" : "pr-6"}`}>
          <div className="flex-1 mr-4">
            <label className="block text-sm text-[#003861] font-medium mb-1">
              Name
            </label>
            <input
              type="text"
              value={medicineName || ""}
              onChange={handleSearchChange}
              placeholder="Search for medicine"
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
              onFocus={() => {
                if (medicineName.trim()) {
                  fetchMedicines(medicineName.trim());
                  setShowMedicineList(true);
                }
              }}
              required
            />
            {showMedicineList && (
              <div className="absolute bg-white border border-gray-300 rounded mt-1 shadow-lg z-10 max-h-52  overflow-auto hiddenScrollbar">
                {filteredMedicines.length > 0 ? (
                  filteredMedicines.map((item) => (
                    <div
                      key={item.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => selectMedicine(item)}
                    >
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        {item.saltDetails}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="p-2 text-gray-500 text-center">
                    No results found
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 mt-6 p-1"
          title="Remove medication"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mb-3 flex flex-row items-center text-sm">
        <label className="block text-sm text-[#003861] font-medium">Salts : </label>
        {salt??"N/A"}
      </div>
      {/* Second Row: Dosage, Details, Duration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Dosage */}
        <div className="flex flex-col">
          <label className="block text-sm text-[#003861] font-medium mb-1">
            Dosage
          </label>
          <div className="flex items-center gap-2">
            <button
              className="border border-gray-300 px-3 py-2 rounded text-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
              onClick={() =>
                onUpdate({ dosage: Math.max(1, medication.dosage - 1) })
              }
            >
              -
            </button>
            <input
              className="w-16 text-center border border-gray-300 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              type="number"
              min="1"
              value={medication.dosage || ""}
              onChange={(e) =>
                onUpdate({ dosage: Math.max(1, parseInt(e.target.value) || 1) })
              }
            />

            <button
              className="border border-gray-300 px-3 py-2 rounded text-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
              onClick={() => onUpdate({ dosage: medication.dosage + 1 })}
            >
              +
            </button>
          </div>
        </div>

        {/* Details */}
        <div className="flex flex-col">
          <label className="block text-sm text-[#003861] font-medium mb-1">
            Details
          </label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            value={medication.details || ""}
            onChange={(e) => onUpdate({ details: e.target.value })}
            placeholder="e.g., After meals"
          />
        </div>

        {/* Duration */}
        <div className="flex flex-col">
          <label className="block text-sm text-[#003861] font-medium mb-1">
            Duration
          </label>
          <div className="flex items-center gap-2">
            <input
              className="w-20 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              type="number"
              min="1"
              value={medication.duration || ""}
              onChange={(e) =>
                onUpdate({
                  duration: Math.max(1, parseInt(e.target.value) || 1),
                })
              }
            />
            <span className="text-sm text-gray-600">days</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicationRow;
